import {UseFormReturn} from 'react-hook-form';

import {GetTrimLevelsApiResponse, RelativeFilterConfigBody} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import type {IsKnownPresetFeature} from './normalizePresetFeatures';
import {
  buildPresetApplyContext,
  PRESET_CONTROLLED_FORM_FIELDS,
  type PresetApplyContext,
  resolvePresetFormFieldValue,
} from './presetFieldResolvers';

function applyPresetFieldValuesToForm(
  formApi: UseFormReturn<FilterFormData>,
  ctx: PresetApplyContext
): void {
  const {setValue} = formApi;

  for (const field of PRESET_CONTROLLED_FORM_FIELDS) {
    setValue(field, resolvePresetFormFieldValue(field, ctx));
  }
}

/**
 * Applies a relative-filter preset config to the existing filter form.
 * Pure function — calls formApi.setValue for each relevant field, then calls onChangeCallback.
 *
 * @param config — Saved relative-filter preset body from the API.
 * @param priceReport — Current report; vehicle and prices drive resolved filter values.
 * @param formApi — React Hook Form instance for the filter form.
 * @param onChangeCallback — Invoked once after all fields are written (e.g. sync URL / parent state).
 * @param trimLevelsCatalog — Catalogue `trim_levels` rows; used only for `same_better` trim mode
 *   (feature scores). Omitted, empty, or incomplete scores for the vehicle trim clear trim selection.
 */
export function applyPresetToForm(
  config: RelativeFilterConfigBody,
  priceReport: PriceReportType,
  formApi: UseFormReturn<FilterFormData>,
  onChangeCallback: () => void,
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null,
  isKnownFeature?: IsKnownPresetFeature
): void {
  const ctx = buildPresetApplyContext(
    config,
    priceReport,
    trimLevelsCatalog,
    isKnownFeature ?? (() => true)
  );
  applyPresetFieldValuesToForm(formApi, ctx);
  onChangeCallback();
}
